import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Table, Modal, Badge } from 'react-bootstrap';
import { FaFileAlt } from 'react-icons/fa';
import { getAuditLogs } from '../../services/Logs/auditLogService';
import DateFilterInput from '../Utils/DateFilterInput';
import UserInput from '../Utils/UserInput';
import { convertDateToISO, getCurrentDate, getCurrentDateMinusDays } from '../Utils/utils';
import { useForm, Controller } from 'react-hook-form';

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [currentFilters, setCurrentFilters] = useState({});

  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      initialDate: getCurrentDateMinusDays(30),
      finalDate: getCurrentDate(),
      userId: null,
    },
  });

  const fetchLogs = useCallback(async (filters = {}, currentPage = page, currentPageSize = pageSize) => {
    setLoading(true);
    try {
      const formattedFilters = {
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.initialDate && { initialDate: convertDateToISO(filters.initialDate) }),
        ...(filters.finalDate && { finalDate: convertDateToISO(filters.finalDate) }),
      };

      const data = await getAuditLogs(formattedFilters, currentPage, currentPageSize);
      setLogs(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);


  const onSubmit = (data) => {
    const filters = {
      userId: data.userId?.id || null,
      initialDate: data.initialDate || null,
      finalDate: data.finalDate || null,
    };
    setCurrentFilters(filters);
    setPage(0);
    fetchLogs(filters, 0, pageSize);
  };

  const handleClear = () => {
    reset({
      initialDate: getCurrentDateMinusDays(30),
      finalDate: getCurrentDate(),
      userId: null,
    });
    setCurrentFilters({});
    setPage(0);
    fetchLogs({}, 0, pageSize);
  };

  const handleViewChanges = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return dateString;
    }
  };

  const getMethodBadgeVariant = (method, route) => {
    if (route === '/auth') {
      return 'primary';
    }
    switch (method) {
      case 'GET':
        return 'info';
      case 'POST':
        return 'success';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getMethodLabel = (method, route) => {
    if (route === '/auth') {
      return 'Acessou';
    }
    switch (method) {
      case 'POST':
        return 'Criou';
      case 'PUT':
        return 'Editou';
      case 'DELETE':
        return 'Removeu';
      case 'GET':
        return 'Visualizou';
      default:
        return method || 'N/A';
    }
  };

  const getStatusBadgeVariant = (status) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'warning';
    if (status >= 400) return 'danger';
    return 'secondary';
  };

  return (
    <Container>
      <h3 className="mb-4 mt-2 text-center text-md-start">Logs do Sistema</h3>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mb-3">
          <Col md={3}>
            <Controller
              name="initialDate"
              control={control}
              rules={{
                required: 'Data inicial é obrigatória',
                validate: (value) => {
                  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                  if (value && !dateRegex.test(value)) return 'Data inválida';
                  return true;
                }
              }}
              render={({ field, fieldState }) => (
                <DateFilterInput
                  label="Data Inicial"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                  required={true}
                />
              )}
            />
          </Col>
          <Col md={3}>
            <Controller
              name="finalDate"
              control={control}
              rules={{
                required: 'Data final é obrigatória',
                validate: (value) => {
                  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                  if (value && !dateRegex.test(value)) return 'Data inválida';
                  return true;
                }
              }}
              render={({ field, fieldState }) => (
                <DateFilterInput
                  label="Data Final"
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                  required={true}
                />
              )}
            />
          </Col>
          <Col md={6}>
            <Row>
              <Col md={8}>
                <Controller
                  name="userId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <UserInput
                      value={field.value}
                      onChange={(selected) => setValue('userId', selected)}
                      label="Usuário"
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <div className="d-flex gap-2 w-100 mb-3">
                  <Button variant="primary" type="submit" className="button-custom flex-fill">
                    Filtrar
                  </Button>
                  <Button variant="secondary" onClick={handleClear} className="flex-fill">
                    Limpar
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </form>

      {loading ? (
        <div className="text-center p-4">Carregando...</div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Data</th>
                <th>Rota</th>
                <th>Ação</th>
                <th>Status</th>
                <th>Visulizar</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">Nenhum log encontrado</td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.username || 'N/A'}</td>
                    <td>{formatDate(log.createdAt)}</td>
                    <td>{log.route || 'N/A'}</td>
                    <td>
                      {log.route === '/auth' ? (
                        <span 
                          className="badge" 
                          style={{ 
                            backgroundColor: '#6f42c1', 
                            color: '#fff', 
                            padding: '0.35em 0.65em',
                            borderRadius: '1rem',
                            fontSize: '0.775em',
                            fontWeight: '700'
                          }}
                        >
                          {getMethodLabel(log.method, log.route)}
                        </span>
                      ) : log.method === 'PUT' ? (
                        <span 
                          className="badge" 
                          style={{ 
                            backgroundColor: '#ff6600', 
                            color: '#fff', 
                            padding: '0.35em 0.65em',
                            borderRadius: '1rem',
                            fontSize: '0.775em',
                            fontWeight: '700'
                          }}
                        >
                          {getMethodLabel(log.method, log.route)}
                        </span>
                      ) : log.method === 'POST' ? (
                        <span 
                          className="badge" 
                          style={{ 
                            backgroundColor: '#198754', 
                            color: '#fff', 
                            padding: '0.35em 0.65em',
                            borderRadius: '1rem',
                            fontSize: '0.775em',
                            fontWeight: '700'
                          }}
                        >
                          {getMethodLabel(log.method, log.route)}
                        </span>
                      ) : (
                        <Badge bg={getMethodBadgeVariant(log.method, log.route)}>
                          {getMethodLabel(log.method, log.route)}
                        </Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg={getStatusBadgeVariant(log.responseStatus)}>
                        {log.responseStatus || 'N/A'}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleViewChanges(log)}
                        disabled={!log.changes || Object.keys(log.changes).length === 0}
                      >
                        Ver Alterações
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Row className="mt-3">
            <Col md={6}>
              <p>
                Mostrando {logs.length} de {totalElements} logs
              </p>
            </Col>
            <Col md={6} className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  const newPage = page - 1;
                  setPage(newPage);
                  fetchLogs(currentFilters, newPage, pageSize);
                }}
                disabled={page === 0 || Object.keys(currentFilters).length === 0}
              >
                Anterior
              </Button>
              <span className="d-flex align-items-center">
                Página {page + 1} de {totalPages || 1}
              </span>
              <Button
                variant="secondary"
                onClick={() => {
                  const newPage = page + 1;
                  setPage(newPage);
                  fetchLogs(currentFilters, newPage, pageSize);
                }}
                disabled={page >= totalPages - 1 || Object.keys(currentFilters).length === 0}
              >
                Próxima
              </Button>
            </Col>
          </Row>
        </>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Alterações do Log</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Usuário:</strong> {selectedLog.username || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Data:</strong> {formatDate(selectedLog.createdAt)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Rota:</strong> {selectedLog.route || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Método:</strong> {selectedLog.method || 'N/A'}
                </Col>
              </Row>
              <hr />
              <h5>Alterações:</h5>
              {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 ? (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Campo</th>
                      <th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(selectedLog.changes).map(([key, value]) => (
                      <tr key={key}>
                        <td><strong>{key}</strong></td>
                        <td>{value || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p>Nenhuma alteração registrada</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Logs;

